/* Generated by scripts/generate-dcs-tools.ts
 * *DO NOT EDIT MANUALLY*
 */
import { z } from 'zod/v3';

export const BashCodeExecution = z.object({ input: z.string() });

export const BashCodeExecutionResponse = z.object({
  content: z.any().superRefine((x, ctx) => {
    const schemas = [
      z.intersection(
        z.object({
          content: z
            .union([z.array(z.object({ file_id: z.string() })), z.null()])
            .optional(),
          return_code: z.number().int(),
          stderr: z.string(),
          stdout: z.string(),
        }),
        z.object({ type: z.literal('bash_code_execution_result') })
      ),
      z.intersection(
        z.object({
          error_code: z.any().superRefine((x, ctx) => {
            const schemas = [
              z.literal('unavailable'),
              z.literal('execution_time_exceeded'),
              z.literal('container_expired'),
              z.literal('invalid_input'),
              z.literal('too_many_requests'),
              z.literal('file_not_found'),
              z.literal('string_not_found'),
            ];
            const errors = schemas.reduce<z.ZodError[]>(
              (errors, schema) =>
                ((result) =>
                  result.error ? [...errors, result.error] : errors)(
                  schema.safeParse(x)
                ),
              []
            );
            if (schemas.length - errors.length !== 1) {
              ctx.addIssue({
                path: ctx.path,
                code: 'invalid_union',
                unionErrors: errors,
                message: 'Invalid input: Should pass single schema',
              });
            }
          }),
        }),
        z.object({ type: z.literal('bash_code_execution_tool_result_error') })
      ),
    ];
    const errors = schemas.reduce<z.ZodError[]>(
      (errors, schema) =>
        ((result) => (result.error ? [...errors, result.error] : errors))(
          schema.safeParse(x)
        ),
      []
    );
    if (schemas.length - errors.length !== 1) {
      ctx.addIssue({
        path: ctx.path,
        code: 'invalid_union',
        unionErrors: errors,
        message: 'Invalid input: Should pass single schema',
      });
    }
  }),
  tool_use_id: z.string(),
});

export const BulkSetEntityPropertyOptions = z.object({
  add_option_ids: z.union([z.array(z.string().uuid()), z.null()]).optional(),
  entities: z.array(
    z.object({
      entity_id: z.string(),
      entity_type: z.enum([
        'document',
        'project',
        'chat',
        'thread',
        'channel',
        'call',
        'user',
        'company',
      ]),
    })
  ),
  property_definition_id: z.string().uuid(),
  remove_option_ids: z.union([z.array(z.string().uuid()), z.null()]).optional(),
});

export const BulkSetEntityPropertyOptionsResponse = z.object({
  results: z.array(
    z.object({
      entityId: z.string(),
      entityType: z.string(),
      error: z.union([z.string(), z.null()]).optional(),
      status: z.string(),
    })
  ),
  summary: z.string(),
});

export const ContentSearch = z.object({
  entityTypes: z
    .array(
      z.enum([
        'documents',
        'chats',
        'emails',
        'channels',
        'projects',
        'call_records',
      ])
    )
    .optional(),
  inbox: z.union([z.string(), z.null()]).optional(),
  matchType: z
    .intersection(
      z.any().superRefine((x, ctx) => {
        const schemas = [z.literal('partial'), z.literal('exact')];
        const errors = schemas.reduce<z.ZodError[]>(
          (errors, schema) =>
            ((result) => (result.error ? [...errors, result.error] : errors))(
              schema.safeParse(x)
            ),
          []
        );
        if (schemas.length - errors.length !== 1) {
          ctx.addIssue({
            path: ctx.path,
            code: 'invalid_union',
            unionErrors: errors,
            message: 'Invalid input: Should pass single schema',
          });
        }
      }),
      z.any().default('partial')
    )
    .optional(),
  query: z.string(),
  tags: z
    .union([
      z.array(
        z.object({
          label: z.string(),
          scope: z
            .union([
              z.any().superRefine((x, ctx) => {
                const schemas = [z.literal('personal'), z.literal('team')];
                const errors = schemas.reduce<z.ZodError[]>(
                  (errors, schema) =>
                    ((result) =>
                      result.error ? [...errors, result.error] : errors)(
                      schema.safeParse(x)
                    ),
                  []
                );
                if (schemas.length - errors.length !== 1) {
                  ctx.addIssue({
                    path: ctx.path,
                    code: 'invalid_union',
                    unionErrors: errors,
                    message: 'Invalid input: Should pass single schema',
                  });
                }
              }),
              z.null(),
            ])
            .optional(),
        })
      ),
      z.null(),
    ])
    .optional(),
  tagsMatch: z
    .any()
    .superRefine((x, ctx) => {
      const schemas = [z.literal('any'), z.literal('all')];
      const errors = schemas.reduce<z.ZodError[]>(
        (errors, schema) =>
          ((result) => (result.error ? [...errors, result.error] : errors))(
            schema.safeParse(x)
          ),
        []
      );
      if (schemas.length - errors.length !== 1) {
        ctx.addIssue({
          path: ctx.path,
          code: 'invalid_union',
          unionErrors: errors,
          message: 'Invalid input: Should pass single schema',
        });
      }
    })
    .optional(),
});

export const SearchToolResponse = z.object({
  results: z.array(
    z
      .object({
        tags: z
          .array(
            z.object({
              label: z.string(),
              scope: z.any().superRefine((x, ctx) => {
                const schemas = [z.literal('personal'), z.literal('team')];
                const errors = schemas.reduce<z.ZodError[]>(
                  (errors, schema) =>
                    ((result) =>
                      result.error ? [...errors, result.error] : errors)(
                      schema.safeParse(x)
                    ),
                  []
                );
                if (schemas.length - errors.length !== 1) {
                  ctx.addIssue({
                    path: ctx.path,
                    code: 'invalid_union',
                    unionErrors: errors,
                    message: 'Invalid input: Should pass single schema',
                  });
                }
              }),
            })
          )
          .optional(),
      })
      .and(
        z.any().superRefine((x, ctx) => {
          const schemas = [
            z.intersection(
              z.object({
                document_id: z.string().uuid(),
                document_name: z.string(),
                document_search_results: z.array(
                  z.object({
                    highlight: z.object({
                      bcc: z.array(z.string()).optional(),
                      cc: z.array(z.string()).optional(),
                      content: z.array(z.string()).optional(),
                      name: z.union([z.string(), z.null()]).optional(),
                      recipients: z.array(z.string()).optional(),
                      sender: z.union([z.string(), z.null()]).optional(),
                      user_id: z.union([z.string(), z.null()]).optional(),
                    }),
                    node_id: z.union([z.string(), z.null()]).optional(),
                    raw_content: z.union([z.string(), z.null()]).optional(),
                    score: z.union([z.number(), z.null()]).optional(),
                  })
                ),
                file_type: z.union([z.string(), z.null()]).optional(),
                id: z.string().uuid(),
                metadata: z
                  .union([
                    z.object({
                      created_at: z.string().datetime({ offset: true }),
                      deleted_at: z
                        .union([
                          z.string().datetime({ offset: true }),
                          z.null(),
                        ])
                        .optional(),
                      project_id: z.union([z.string(), z.null()]).optional(),
                      updated_at: z.string().datetime({ offset: true }),
                      viewed_at: z
                        .union([
                          z.string().datetime({ offset: true }),
                          z.null(),
                        ])
                        .optional(),
                    }),
                    z.null(),
                  ])
                  .optional(),
                name: z.string(),
                owner_id: z.string(),
                sub_type: z
                  .union([
                    z.any().superRefine((x, ctx) => {
                      const schemas = [
                        z.literal('task'),
                        z.literal('snippet'),
                        z.literal('skill'),
                      ];
                      const errors = schemas.reduce<z.ZodError[]>(
                        (errors, schema) =>
                          ((result) =>
                            result.error ? [...errors, result.error] : errors)(
                            schema.safeParse(x)
                          ),
                        []
                      );
                      if (schemas.length - errors.length !== 1) {
                        ctx.addIssue({
                          path: ctx.path,
                          code: 'invalid_union',
                          unionErrors: errors,
                          message: 'Invalid input: Should pass single schema',
                        });
                      }
                    }),
                    z.null(),
                  ])
                  .optional(),
              }),
              z.object({ type: z.literal('document') })
            ),
            z.intersection(
              z.object({
                chat_id: z.string().uuid(),
                chat_search_results: z.array(
                  z.object({
                    chat_message_id: z
                      .union([z.string().uuid(), z.null()])
                      .optional(),
                    highlight: z.object({
                      bcc: z.array(z.string()).optional(),
                      cc: z.array(z.string()).optional(),
                      content: z.array(z.string()).optional(),
                      name: z.union([z.string(), z.null()]).optional(),
                      recipients: z.array(z.string()).optional(),
                      sender: z.union([z.string(), z.null()]).optional(),
                      user_id: z.union([z.string(), z.null()]).optional(),
                    }),
                    role: z.union([z.string(), z.null()]).optional(),
                    score: z.union([z.number(), z.null()]).optional(),
                  })
                ),
                id: z.string().uuid(),
                metadata: z
                  .union([
                    z.object({
                      created_at: z.string().datetime({ offset: true }),
                      deleted_at: z
                        .union([
                          z.string().datetime({ offset: true }),
                          z.null(),
                        ])
                        .optional(),
                      project_id: z.union([z.string(), z.null()]).optional(),
                      updated_at: z.string().datetime({ offset: true }),
                      viewed_at: z
                        .union([
                          z.string().datetime({ offset: true }),
                          z.null(),
                        ])
                        .optional(),
                    }),
                    z.null(),
                  ])
                  .optional(),
                name: z.string(),
                owner_id: z.string(),
                user_id: z.string(),
              }),
              z.object({ type: z.literal('chat') })
            ),
            z.intersection(
              z.object({
                created_at: z.string().datetime({ offset: true }),
                email_message_search_results: z.array(
                  z.object({
                    bcc: z.array(z.string()),
                    cc: z.array(z.string()),
                    highlight: z.object({
                      bcc: z.array(z.string()).optional(),
                      cc: z.array(z.string()).optional(),
                      content: z.array(z.string()).optional(),
                      name: z.union([z.string(), z.null()]).optional(),
                      recipients: z.array(z.string()).optional(),
                      sender: z.union([z.string(), z.null()]).optional(),
                      user_id: z.union([z.string(), z.null()]).optional(),
                    }),
                    labels: z.array(z.string()),
                    message_id: z
                      .union([z.string().uuid(), z.null()])
                      .optional(),
                    pretty_sender: z.string(),
                    recipients: z.array(z.string()),
                    score: z.union([z.number(), z.null()]).optional(),
                    sender: z.string(),
                    sent_at: z
                      .union([z.string().datetime({ offset: true }), z.null()])
                      .optional(),
                  })
                ),
                id: z.string().uuid(),
                inbox_visible: z.boolean(),
                is_draft: z.boolean(),
                is_important: z.boolean(),
                is_read: z.boolean(),
                link_id: z.string().uuid(),
                name: z.union([z.string(), z.null()]).optional(),
                owner_id: z.string(),
                participants: z.array(
                  z.object({
                    email: z.string(),
                    name: z.union([z.string(), z.null()]).optional(),
                  })
                ),
                snippet: z.union([z.string(), z.null()]).optional(),
                subject: z.union([z.string(), z.null()]).optional(),
                thread_id: z.string().uuid(),
                updated_at: z.string().datetime({ offset: true }),
                user_id: z.string(),
                viewed_at: z
                  .union([z.string().datetime({ offset: true }), z.null()])
                  .optional(),
              }),
              z.object({ type: z.literal('email') })
            ),
            z.intersection(
              z.object({
                channel_id: z.string().uuid(),
                channel_type: z.string(),
                created_at: z.string().datetime({ offset: true }),
                deleted_at: z
                  .union([z.string().datetime({ offset: true }), z.null()])
                  .optional(),
                highlight: z.object({
                  bcc: z.array(z.string()).optional(),
                  cc: z.array(z.string()).optional(),
                  content: z.array(z.string()).optional(),
                  name: z.union([z.string(), z.null()]).optional(),
                  recipients: z.array(z.string()).optional(),
                  sender: z.union([z.string(), z.null()]).optional(),
                  user_id: z.union([z.string(), z.null()]).optional(),
                }),
                id: z.string().uuid(),
                message_id: z.string().uuid(),
                owner_id: z.union([z.string(), z.null()]).optional(),
                score: z.union([z.number(), z.null()]).optional(),
                sender_id: z.string(),
                thread_id: z.union([z.string().uuid(), z.null()]).optional(),
                updated_at: z.string().datetime({ offset: true }),
              }),
              z.object({ type: z.literal('channelMessage') })
            ),
            z.intersection(
              z.object({
                channel_id: z.string().uuid(),
                channel_type: z.string(),
                highlight: z.object({
                  bcc: z.array(z.string()).optional(),
                  cc: z.array(z.string()).optional(),
                  content: z.array(z.string()).optional(),
                  name: z.union([z.string(), z.null()]).optional(),
                  recipients: z.array(z.string()).optional(),
                  sender: z.union([z.string(), z.null()]).optional(),
                  user_id: z.union([z.string(), z.null()]).optional(),
                }),
                id: z.string().uuid(),
                metadata: z
                  .union([
                    z.object({
                      created_at: z.string().datetime({ offset: true }),
                      interacted_at: z
                        .union([
                          z.string().datetime({ offset: true }),
                          z.null(),
                        ])
                        .optional(),
                      updated_at: z.string().datetime({ offset: true }),
                      viewed_at: z
                        .union([
                          z.string().datetime({ offset: true }),
                          z.null(),
                        ])
                        .optional(),
                    }),
                    z.null(),
                  ])
                  .optional(),
                owner_id: z.union([z.string(), z.null()]).optional(),
                score: z.union([z.number(), z.null()]).optional(),
              }),
              z.object({ type: z.literal('channel') })
            ),
            z.intersection(
              z.object({
                created_at: z.string().datetime({ offset: true }),
                id: z.string().uuid(),
                metadata: z
                  .union([
                    z.object({
                      created_at: z.string().datetime({ offset: true }),
                      deleted_at: z
                        .union([
                          z.string().datetime({ offset: true }),
                          z.null(),
                        ])
                        .optional(),
                      parent_project_id: z
                        .union([z.string(), z.null()])
                        .optional(),
                      updated_at: z.string().datetime({ offset: true }),
                      viewed_at: z
                        .union([
                          z.string().datetime({ offset: true }),
                          z.null(),
                        ])
                        .optional(),
                    }),
                    z.null(),
                  ])
                  .optional(),
                name: z.string(),
                owner_id: z.string(),
                project_search_results: z.array(
                  z.object({
                    highlight: z.object({
                      bcc: z.array(z.string()).optional(),
                      cc: z.array(z.string()).optional(),
                      content: z.array(z.string()).optional(),
                      name: z.union([z.string(), z.null()]).optional(),
                      recipients: z.array(z.string()).optional(),
                      sender: z.union([z.string(), z.null()]).optional(),
                      user_id: z.union([z.string(), z.null()]).optional(),
                    }),
                    score: z.union([z.number(), z.null()]).optional(),
                  })
                ),
                updated_at: z.string().datetime({ offset: true }),
              }),
              z.object({ type: z.literal('project') })
            ),
            z.intersection(
              z.object({
                call_id: z.string().uuid(),
                call_search_results: z.array(
                  z.object({
                    ended_at: z
                      .union([z.string().datetime({ offset: true }), z.null()])
                      .optional(),
                    highlight: z.object({
                      bcc: z.array(z.string()).optional(),
                      cc: z.array(z.string()).optional(),
                      content: z.array(z.string()).optional(),
                      name: z.union([z.string(), z.null()]).optional(),
                      recipients: z.array(z.string()).optional(),
                      sender: z.union([z.string(), z.null()]).optional(),
                      user_id: z.union([z.string(), z.null()]).optional(),
                    }),
                    score: z.union([z.number(), z.null()]).optional(),
                    sequence_num: z
                      .union([z.number().int(), z.null()])
                      .optional(),
                    speaker_id: z.union([z.string(), z.null()]).optional(),
                    started_at: z
                      .union([z.string().datetime({ offset: true }), z.null()])
                      .optional(),
                    transcript_id: z
                      .union([z.string().uuid(), z.null()])
                      .optional(),
                  })
                ),
                channel_id: z.string().uuid(),
                id: z.string().uuid(),
                metadata: z
                  .union([
                    z.object({
                      attended: z.boolean(),
                      channel_name: z.union([z.string(), z.null()]).optional(),
                      created_by: z.string(),
                      duration_ms: z.number().int(),
                      ended_at: z.string().datetime({ offset: true }),
                      started_at: z.string().datetime({ offset: true }),
                      status: z.any().superRefine((x, ctx) => {
                        const schemas = [
                          z.literal('ATTENDED'),
                          z.literal('MISSED'),
                          z.literal('UNATTENDED'),
                        ];
                        const errors = schemas.reduce<z.ZodError[]>(
                          (errors, schema) =>
                            ((result) =>
                              result.error
                                ? [...errors, result.error]
                                : errors)(schema.safeParse(x)),
                          []
                        );
                        if (schemas.length - errors.length !== 1) {
                          ctx.addIssue({
                            path: ctx.path,
                            code: 'invalid_union',
                            unionErrors: errors,
                            message: 'Invalid input: Should pass single schema',
                          });
                        }
                      }),
                      updated_at: z.string().datetime({ offset: true }),
                    }),
                    z.null(),
                  ])
                  .optional(),
                name: z.union([z.string(), z.null()]).optional(),
                owner_id: z.string(),
                participant_ids: z.array(z.string()),
              }),
              z.object({ type: z.literal('call') })
            ),
            z.intersection(
              z.object({
                createdAt: z.string().datetime({ offset: true }),
                description: z.union([z.string(), z.null()]).optional(),
                domains: z.array(
                  z.object({
                    companyId: z.string().uuid(),
                    createdAt: z.string().datetime({ offset: true }),
                    domain: z.string(),
                    id: z.string().uuid(),
                  })
                ),
                hidden: z.boolean(),
                id: z.string().uuid(),
                name: z.union([z.string(), z.null()]).optional(),
                nameHighlighted: z.union([z.string(), z.null()]).optional(),
                teamId: z.string().uuid(),
                updatedAt: z.string().datetime({ offset: true }),
              }),
              z.object({ type: z.literal('company') })
            ),
          ];
          const errors = schemas.reduce<z.ZodError[]>(
            (errors, schema) =>
              ((result) => (result.error ? [...errors, result.error] : errors))(
                schema.safeParse(x)
              ),
            []
          );
          if (schemas.length - errors.length !== 1) {
            ctx.addIssue({
              path: ctx.path,
              code: 'invalid_union',
              unionErrors: errors,
              message: 'Invalid input: Should pass single schema',
            });
          }
        })
      )
  ),
});

export const CreateDocument = z.object({
  documentName: z.string(),
  fileContent: z.string(),
  fileExtension: z.string(),
  isTask: z.boolean().optional(),
  projectId: z.union([z.string().uuid(), z.null()]).optional(),
});

export const CreateDocumentResponse = z.object({
  documentId: z.string().uuid(),
});

export const CreateImportEntity = z.object({
  entityId: z.union([z.string(), z.null()]).optional(),
  foreignId: z.string(),
  metadata: z.any(),
  source: z.any().superRefine((x, ctx) => {
    const schemas = [
      z.literal('linear'),
      z.literal('notion'),
      z.literal('slack'),
    ];
    const errors = schemas.reduce<z.ZodError[]>(
      (errors, schema) =>
        ((result) => (result.error ? [...errors, result.error] : errors))(
          schema.safeParse(x)
        ),
      []
    );
    if (schemas.length - errors.length !== 1) {
      ctx.addIssue({
        path: ctx.path,
        code: 'invalid_union',
        unionErrors: errors,
        message: 'Invalid input: Should pass single schema',
      });
    }
  }),
  status: z.any().superRefine((x, ctx) => {
    const schemas = [z.literal('staged'), z.literal('imported')];
    const errors = schemas.reduce<z.ZodError[]>(
      (errors, schema) =>
        ((result) => (result.error ? [...errors, result.error] : errors))(
          schema.safeParse(x)
        ),
      []
    );
    if (schemas.length - errors.length !== 1) {
      ctx.addIssue({
        path: ctx.path,
        code: 'invalid_union',
        unionErrors: errors,
        message: 'Invalid input: Should pass single schema',
      });
    }
  }),
});

export const CreateImportEntityResponse = z.object({
  entity: z.object({
    entityId: z.union([z.string(), z.null()]).optional(),
    entityType: z.union([z.string(), z.null()]).optional(),
    foreignId: z.string(),
    id: z.string().uuid(),
    importedByTeammate: z.boolean(),
    label: z.string(),
    source: z.any().superRefine((x, ctx) => {
      const schemas = [
        z.literal('linear'),
        z.literal('notion'),
        z.literal('slack'),
      ];
      const errors = schemas.reduce<z.ZodError[]>(
        (errors, schema) =>
          ((result) => (result.error ? [...errors, result.error] : errors))(
            schema.safeParse(x)
          ),
        []
      );
      if (schemas.length - errors.length !== 1) {
        ctx.addIssue({
          path: ctx.path,
          code: 'invalid_union',
          unionErrors: errors,
          message: 'Invalid input: Should pass single schema',
        });
      }
    }),
    status: z.any().superRefine((x, ctx) => {
      const schemas = [
        z.literal('staged'),
        z.literal('importing'),
        z.literal('imported'),
        z.literal('discarded'),
      ];
      const errors = schemas.reduce<z.ZodError[]>(
        (errors, schema) =>
          ((result) => (result.error ? [...errors, result.error] : errors))(
            schema.safeParse(x)
          ),
        []
      );
      if (schemas.length - errors.length !== 1) {
        ctx.addIssue({
          path: ctx.path,
          code: 'invalid_union',
          unionErrors: errors,
          message: 'Invalid input: Should pass single schema',
        });
      }
    }),
  }),
  message: z.string(),
  outcome: z.string(),
});

export const CreateProject = z.object({
  parentProjectId: z.union([z.string().uuid(), z.null()]).optional(),
  projectName: z.string(),
});

export const CreateProjectResponse = z.object({
  projectId: z.string().uuid(),
  projectName: z.string(),
});

export const CreateTag = z.object({
  color: z.any().superRefine((x, ctx) => {
    const schemas = [
      z.literal('red'),
      z.literal('tomato'),
      z.literal('orange'),
      z.literal('amber'),
      z.literal('yellow'),
      z.literal('green'),
      z.literal('teal'),
      z.literal('blue'),
      z.literal('indigo'),
      z.literal('purple'),
      z.literal('pink'),
      z.literal('gray'),
    ];
    const errors = schemas.reduce<z.ZodError[]>(
      (errors, schema) =>
        ((result) => (result.error ? [...errors, result.error] : errors))(
          schema.safeParse(x)
        ),
      []
    );
    if (schemas.length - errors.length !== 1) {
      ctx.addIssue({
        path: ctx.path,
        code: 'invalid_union',
        unionErrors: errors,
        message: 'Invalid input: Should pass single schema',
      });
    }
  }),
  label: z.string(),
  scope: z
    .intersection(
      z.any().superRefine((x, ctx) => {
        const schemas = [z.literal('personal'), z.literal('team')];
        const errors = schemas.reduce<z.ZodError[]>(
          (errors, schema) =>
            ((result) => (result.error ? [...errors, result.error] : errors))(
              schema.safeParse(x)
            ),
          []
        );
        if (schemas.length - errors.length !== 1) {
          ctx.addIssue({
            path: ctx.path,
            code: 'invalid_union',
            unionErrors: errors,
            message: 'Invalid input: Should pass single schema',
          });
        }
      }),
      z.any().default('personal')
    )
    .optional(),
});

export const CreateTagResponse = z.object({
  color: z.union([z.string(), z.null()]).optional(),
  id: z.string().uuid(),
  label: z.string(),
  propertyDefinitionId: z.string().uuid(),
  scope: z.any().superRefine((x, ctx) => {
    const schemas = [z.literal('personal'), z.literal('team')];
    const errors = schemas.reduce<z.ZodError[]>(
      (errors, schema) =>
        ((result) => (result.error ? [...errors, result.error] : errors))(
          schema.safeParse(x)
        ),
      []
    );
    if (schemas.length - errors.length !== 1) {
      ctx.addIssue({
        path: ctx.path,
        code: 'invalid_union',
        unionErrors: errors,
        message: 'Invalid input: Should pass single schema',
      });
    }
  }),
  summary: z.string(),
});

export const DeleteImportEntity = z.object({ id: z.string().uuid() });

export const DeleteImportEntityResponse = z.object({
  discarded: z.boolean(),
  message: z.string(),
});

export const DeleteTag = z.object({
  id: z.string().uuid(),
  property_definition_id: z.string().uuid(),
});

export const DeleteTagResponse = z.object({
  message: z.string(),
  success: z.boolean(),
});

export const DisplayResults = z.object({ view: z.any() });

export const DisplayResultsResponse = z.object({ message: z.string() });

export const EditDocument = z.object({
  document_id: z.string(),
  instructions: z.string(),
});

export const EditDocumentResponse = z.object({
  clarification: z.union([z.string(), z.null()]).optional(),
  summary: z.string(),
});

export const EditTag = z.object({
  color: z
    .union([
      z.any().superRefine((x, ctx) => {
        const schemas = [
          z.literal('red'),
          z.literal('tomato'),
          z.literal('orange'),
          z.literal('amber'),
          z.literal('yellow'),
          z.literal('green'),
          z.literal('teal'),
          z.literal('blue'),
          z.literal('indigo'),
          z.literal('purple'),
          z.literal('pink'),
          z.literal('gray'),
        ];
        const errors = schemas.reduce<z.ZodError[]>(
          (errors, schema) =>
            ((result) => (result.error ? [...errors, result.error] : errors))(
              schema.safeParse(x)
            ),
          []
        );
        if (schemas.length - errors.length !== 1) {
          ctx.addIssue({
            path: ctx.path,
            code: 'invalid_union',
            unionErrors: errors,
            message: 'Invalid input: Should pass single schema',
          });
        }
      }),
      z.null(),
    ])
    .optional(),
  id: z.string().uuid(),
  label: z.union([z.string(), z.null()]).optional(),
  property_definition_id: z.string().uuid(),
});

export const EditTagResponse = z.object({
  color: z.union([z.string(), z.null()]).optional(),
  id: z.string().uuid(),
  label: z.string(),
  propertyDefinitionId: z.string().uuid(),
  summary: z.string(),
});

export const GetCompany = z.object({ company_id: z.string().uuid() });

export const GetCompanyResponse = z.object({
  contacts: z.array(
    z.object({
      email: z.string(),
      id: z.string().uuid(),
      lastInteraction: z.string().datetime({ offset: true }),
      name: z.union([z.string(), z.null()]).optional(),
    })
  ),
  description: z.union([z.string(), z.null()]).optional(),
  domains: z.array(z.string()),
  emailSync: z.boolean(),
  firstInteraction: z.string().datetime({ offset: true }),
  hidden: z.boolean(),
  id: z.string().uuid(),
  lastInteraction: z.string().datetime({ offset: true }),
  name: z.union([z.string(), z.null()]).optional(),
  ownerUserId: z.union([z.string(), z.null()]).optional(),
  properties: z.array(
    z.object({
      currentValue: z.any().optional(),
      dataType: z.string(),
      displayName: z.string(),
      isMultiSelect: z.boolean(),
      isSystem: z.boolean(),
      options: z.array(
        z.object({ displayValue: z.string(), id: z.string().uuid() })
      ),
      propertyDefinitionId: z.string().uuid(),
    })
  ),
  revenue: z.union([z.number(), z.null()]).optional(),
  stage: z
    .union([
      z.object({ label: z.string(), optionId: z.string().uuid() }),
      z.null(),
    ])
    .optional(),
  summary: z.string(),
});

export const GetEntityProperties = z.object({
  entity_id: z.string(),
  entity_type: z.enum([
    'document',
    'project',
    'chat',
    'thread',
    'channel',
    'call',
    'user',
    'company',
  ]),
});

export const GetEntityPropertiesResponse = z.object({
  properties: z.array(
    z.object({
      currentValue: z.any().optional(),
      currentValueLabels: z.union([z.array(z.string()), z.null()]).optional(),
      dataType: z.string(),
      displayName: z.string(),
      isMultiSelect: z.boolean(),
      isSystem: z.boolean(),
      options: z
        .array(
          z.object({
            displayOrder: z.number().int(),
            displayValue: z.string(),
            id: z.string().uuid(),
          })
        )
        .optional(),
      propertyDefinitionId: z.string().uuid(),
      scope: z
        .union([
          z.any().superRefine((x, ctx) => {
            const schemas = [z.literal('personal'), z.literal('team')];
            const errors = schemas.reduce<z.ZodError[]>(
              (errors, schema) =>
                ((result) =>
                  result.error ? [...errors, result.error] : errors)(
                  schema.safeParse(x)
                ),
              []
            );
            if (schemas.length - errors.length !== 1) {
              ctx.addIssue({
                path: ctx.path,
                code: 'invalid_union',
                unionErrors: errors,
                message: 'Invalid input: Should pass single schema',
              });
            }
          }),
          z.null(),
        ])
        .optional(),
    })
  ),
  summary: z.string(),
});

export const GetThread = z.object({
  limit: z.union([z.number().int(), z.null()]).optional(),
  threadId: z.string().uuid(),
});

export const GetThreadResponse = z.object({
  isRead: z.boolean(),
  labels: z.array(z.string()),
  messages: z.array(
    z.object({
      bodyParsed: z.union([z.string(), z.null()]).optional(),
      cc: z.array(
        z.object({
          email: z.string(),
          name: z.union([z.string(), z.null()]).optional(),
        })
      ),
      date: z.union([z.string(), z.null()]).optional(),
      from: z
        .union([
          z.object({
            email: z.string(),
            name: z.union([z.string(), z.null()]).optional(),
          }),
          z.null(),
        ])
        .optional(),
      id: z.string().uuid(),
      labels: z.array(z.string()),
      subject: z.union([z.string(), z.null()]).optional(),
      to: z.array(
        z.object({
          email: z.string(),
          name: z.union([z.string(), z.null()]).optional(),
        })
      ),
    })
  ),
  summary: z.string(),
  threadId: z.string().uuid(),
});

export const ImportNotionPage = z.object({ pageUrl: z.string() });

export const ImportNotionPageResponse = z.object({
  entity: z.object({
    entityId: z.union([z.string(), z.null()]).optional(),
    entityType: z.union([z.string(), z.null()]).optional(),
    foreignId: z.string(),
    id: z.string().uuid(),
    importedByTeammate: z.boolean(),
    label: z.string(),
    source: z.any().superRefine((x, ctx) => {
      const schemas = [
        z.literal('linear'),
        z.literal('notion'),
        z.literal('slack'),
      ];
      const errors = schemas.reduce<z.ZodError[]>(
        (errors, schema) =>
          ((result) => (result.error ? [...errors, result.error] : errors))(
            schema.safeParse(x)
          ),
        []
      );
      if (schemas.length - errors.length !== 1) {
        ctx.addIssue({
          path: ctx.path,
          code: 'invalid_union',
          unionErrors: errors,
          message: 'Invalid input: Should pass single schema',
        });
      }
    }),
    status: z.any().superRefine((x, ctx) => {
      const schemas = [
        z.literal('staged'),
        z.literal('importing'),
        z.literal('imported'),
        z.literal('discarded'),
      ];
      const errors = schemas.reduce<z.ZodError[]>(
        (errors, schema) =>
          ((result) => (result.error ? [...errors, result.error] : errors))(
            schema.safeParse(x)
          ),
        []
      );
      if (schemas.length - errors.length !== 1) {
        ctx.addIssue({
          path: ctx.path,
          code: 'invalid_union',
          unionErrors: errors,
          message: 'Invalid input: Should pass single schema',
        });
      }
    }),
  }),
  message: z.string(),
  outcome: z.string(),
});

export const ListCompanies = z.object({
  include_hidden: z.union([z.boolean(), z.null()]).optional(),
  limit: z.union([z.number().int().gte(0).lte(65535), z.null()]).optional(),
  owner_user_id: z.union([z.string(), z.null()]).optional(),
  search: z.union([z.string(), z.null()]).optional(),
  stage: z.union([z.string(), z.null()]).optional(),
});

export const ListCompaniesResponse = z.object({
  companies: z.array(
    z.object({
      domains: z.array(z.string()),
      hidden: z.boolean(),
      id: z.string().uuid(),
      lastInteraction: z.string().datetime({ offset: true }),
      name: z.union([z.string(), z.null()]).optional(),
      ownerUserId: z.union([z.string(), z.null()]).optional(),
      revenue: z.union([z.number(), z.null()]).optional(),
      stage: z
        .union([
          z.object({ label: z.string(), optionId: z.string().uuid() }),
          z.null(),
        ])
        .optional(),
    })
  ),
  summary: z.string(),
});

export const ListEntities = z.object({
  callf: z.any().optional(),
  cf: z.any().optional(),
  chanf: z.any().optional(),
  cthf: z.any().optional(),
  df: z.any().optional(),
  ef: z.any().optional(),
  emailPreset: z.union([z.literal('signal'), z.null()]).optional(),
  emailView: z.union([z.string(), z.null()]).optional(),
  fef: z.any().optional(),
  inbox: z.union([z.string(), z.null()]).optional(),
  includeTypes: z
    .union([
      z.array(
        z.any().superRefine((x, ctx) => {
          const schemas = [
            z.literal('calendar_event'),
            z.literal('document'),
            z.literal('ai_chat'),
            z.literal('project'),
            z.literal('email'),
            z.literal('channel'),
            z.literal('channel_thread'),
            z.literal('call'),
            z.literal('foreign_entity'),
          ];
          const errors = schemas.reduce<z.ZodError[]>(
            (errors, schema) =>
              ((result) => (result.error ? [...errors, result.error] : errors))(
                schema.safeParse(x)
              ),
            []
          );
          if (schemas.length - errors.length !== 1) {
            ctx.addIssue({
              path: ctx.path,
              code: 'invalid_union',
              unionErrors: errors,
              message: 'Invalid input: Should pass single schema',
            });
          }
        })
      ),
      z.null(),
    ])
    .optional(),
  limit: z.union([z.number().int().gte(0).lte(65535), z.null()]).optional(),
  pf: z.any().optional(),
  propf: z.any().optional(),
  sortBy: z
    .any()
    .superRefine((x, ctx) => {
      const schemas = [
        z.literal('recently_viewed'),
        z.literal('recently_updated'),
        z.literal('recently_created'),
      ];
      const errors = schemas.reduce<z.ZodError[]>(
        (errors, schema) =>
          ((result) => (result.error ? [...errors, result.error] : errors))(
            schema.safeParse(x)
          ),
        []
      );
      if (schemas.length - errors.length !== 1) {
        ctx.addIssue({
          path: ctx.path,
          code: 'invalid_union',
          unionErrors: errors,
          message: 'Invalid input: Should pass single schema',
        });
      }
    })
    .optional(),
  tags: z
    .union([
      z.array(
        z.object({
          label: z.string(),
          scope: z
            .union([
              z.any().superRefine((x, ctx) => {
                const schemas = [z.literal('personal'), z.literal('team')];
                const errors = schemas.reduce<z.ZodError[]>(
                  (errors, schema) =>
                    ((result) =>
                      result.error ? [...errors, result.error] : errors)(
                      schema.safeParse(x)
                    ),
                  []
                );
                if (schemas.length - errors.length !== 1) {
                  ctx.addIssue({
                    path: ctx.path,
                    code: 'invalid_union',
                    unionErrors: errors,
                    message: 'Invalid input: Should pass single schema',
                  });
                }
              }),
              z.null(),
            ])
            .optional(),
        })
      ),
      z.null(),
    ])
    .optional(),
  tagsMatch: z
    .any()
    .superRefine((x, ctx) => {
      const schemas = [z.literal('any'), z.literal('all')];
      const errors = schemas.reduce<z.ZodError[]>(
        (errors, schema) =>
          ((result) => (result.error ? [...errors, result.error] : errors))(
            schema.safeParse(x)
          ),
        []
      );
      if (schemas.length - errors.length !== 1) {
        ctx.addIssue({
          path: ctx.path,
          code: 'invalid_union',
          unionErrors: errors,
          message: 'Invalid input: Should pass single schema',
        });
      }
    })
    .optional(),
});

export const ListEntitiesResponse = z.object({
  items: z.array(
    z.any().superRefine((x, ctx) => {
      const schemas = [
        z.object({
          conferenceUrl: z.union([z.string(), z.null()]).optional(),
          id: z.string().uuid(),
          location: z.union([z.string(), z.null()]).optional(),
          status: z.string(),
          tags: z
            .array(
              z.object({
                label: z.string(),
                scope: z.any().superRefine((x, ctx) => {
                  const schemas = [z.literal('personal'), z.literal('team')];
                  const errors = schemas.reduce<z.ZodError[]>(
                    (errors, schema) =>
                      ((result) =>
                        result.error ? [...errors, result.error] : errors)(
                        schema.safeParse(x)
                      ),
                    []
                  );
                  if (schemas.length - errors.length !== 1) {
                    ctx.addIssue({
                      path: ctx.path,
                      code: 'invalid_union',
                      unionErrors: errors,
                      message: 'Invalid input: Should pass single schema',
                    });
                  }
                }),
              })
            )
            .optional(),
          time: z.any(),
          title: z.string(),
          type: z.literal('calendarEvent'),
        }),
        z.object({
          fileType: z.union([z.string(), z.null()]).optional(),
          id: z.string().uuid(),
          name: z.string(),
          subType: z.union([z.string(), z.null()]).optional(),
          tags: z
            .array(
              z.object({
                label: z.string(),
                scope: z.any().superRefine((x, ctx) => {
                  const schemas = [z.literal('personal'), z.literal('team')];
                  const errors = schemas.reduce<z.ZodError[]>(
                    (errors, schema) =>
                      ((result) =>
                        result.error ? [...errors, result.error] : errors)(
                        schema.safeParse(x)
                      ),
                    []
                  );
                  if (schemas.length - errors.length !== 1) {
                    ctx.addIssue({
                      path: ctx.path,
                      code: 'invalid_union',
                      unionErrors: errors,
                      message: 'Invalid input: Should pass single schema',
                    });
                  }
                }),
              })
            )
            .optional(),
          type: z.literal('document'),
        }),
        z.object({
          id: z.string().uuid(),
          name: z.string(),
          tags: z
            .array(
              z.object({
                label: z.string(),
                scope: z.any().superRefine((x, ctx) => {
                  const schemas = [z.literal('personal'), z.literal('team')];
                  const errors = schemas.reduce<z.ZodError[]>(
                    (errors, schema) =>
                      ((result) =>
                        result.error ? [...errors, result.error] : errors)(
                        schema.safeParse(x)
                      ),
                    []
                  );
                  if (schemas.length - errors.length !== 1) {
                    ctx.addIssue({
                      path: ctx.path,
                      code: 'invalid_union',
                      unionErrors: errors,
                      message: 'Invalid input: Should pass single schema',
                    });
                  }
                }),
              })
            )
            .optional(),
          type: z.literal('aiChat'),
        }),
        z.object({
          id: z.string().uuid(),
          name: z.string(),
          tags: z
            .array(
              z.object({
                label: z.string(),
                scope: z.any().superRefine((x, ctx) => {
                  const schemas = [z.literal('personal'), z.literal('team')];
                  const errors = schemas.reduce<z.ZodError[]>(
                    (errors, schema) =>
                      ((result) =>
                        result.error ? [...errors, result.error] : errors)(
                        schema.safeParse(x)
                      ),
                    []
                  );
                  if (schemas.length - errors.length !== 1) {
                    ctx.addIssue({
                      path: ctx.path,
                      code: 'invalid_union',
                      unionErrors: errors,
                      message: 'Invalid input: Should pass single schema',
                    });
                  }
                }),
              })
            )
            .optional(),
          type: z.literal('project'),
        }),
        z.object({
          id: z.string().uuid(),
          inboxVisible: z.boolean(),
          isDraft: z.boolean(),
          isRead: z.boolean(),
          senderEmail: z.union([z.string(), z.null()]).optional(),
          senderName: z.union([z.string(), z.null()]).optional(),
          snippet: z.union([z.string(), z.null()]).optional(),
          subject: z.union([z.string(), z.null()]).optional(),
          tags: z
            .array(
              z.object({
                label: z.string(),
                scope: z.any().superRefine((x, ctx) => {
                  const schemas = [z.literal('personal'), z.literal('team')];
                  const errors = schemas.reduce<z.ZodError[]>(
                    (errors, schema) =>
                      ((result) =>
                        result.error ? [...errors, result.error] : errors)(
                        schema.safeParse(x)
                      ),
                    []
                  );
                  if (schemas.length - errors.length !== 1) {
                    ctx.addIssue({
                      path: ctx.path,
                      code: 'invalid_union',
                      unionErrors: errors,
                      message: 'Invalid input: Should pass single schema',
                    });
                  }
                }),
              })
            )
            .optional(),
          type: z.literal('email'),
        }),
        z.object({
          id: z.string().uuid(),
          name: z.union([z.string(), z.null()]).optional(),
          type: z.literal('channel'),
        }),
        z.object({
          channelId: z.string().uuid(),
          id: z.string().uuid(),
          type: z.literal('channelThread'),
        }),
        z.object({
          createdBy: z.string(),
          id: z.string().uuid(),
          tags: z
            .array(
              z.object({
                label: z.string(),
                scope: z.any().superRefine((x, ctx) => {
                  const schemas = [z.literal('personal'), z.literal('team')];
                  const errors = schemas.reduce<z.ZodError[]>(
                    (errors, schema) =>
                      ((result) =>
                        result.error ? [...errors, result.error] : errors)(
                        schema.safeParse(x)
                      ),
                    []
                  );
                  if (schemas.length - errors.length !== 1) {
                    ctx.addIssue({
                      path: ctx.path,
                      code: 'invalid_union',
                      unionErrors: errors,
                      message: 'Invalid input: Should pass single schema',
                    });
                  }
                }),
              })
            )
            .optional(),
          type: z.literal('call'),
        }),
        z.object({
          foreignEntityId: z.string(),
          foreignEntitySource: z.string(),
          id: z.string().uuid(),
          metadata: z.any(),
          type: z.literal('foreignEntity'),
        }),
      ];
      const errors = schemas.reduce<z.ZodError[]>(
        (errors, schema) =>
          ((result) => (result.error ? [...errors, result.error] : errors))(
            schema.safeParse(x)
          ),
        []
      );
      if (schemas.length - errors.length !== 1) {
        ctx.addIssue({
          path: ctx.path,
          code: 'invalid_union',
          unionErrors: errors,
          message: 'Invalid input: Should pass single schema',
        });
      }
    })
  ),
  summary: z.string(),
});

export const ListImportEntities = z.object({
  source: z
    .union([
      z.any().superRefine((x, ctx) => {
        const schemas = [
          z.literal('linear'),
          z.literal('notion'),
          z.literal('slack'),
        ];
        const errors = schemas.reduce<z.ZodError[]>(
          (errors, schema) =>
            ((result) => (result.error ? [...errors, result.error] : errors))(
              schema.safeParse(x)
            ),
          []
        );
        if (schemas.length - errors.length !== 1) {
          ctx.addIssue({
            path: ctx.path,
            code: 'invalid_union',
            unionErrors: errors,
            message: 'Invalid input: Should pass single schema',
          });
        }
      }),
      z.null(),
    ])
    .optional(),
  status: z
    .union([
      z.any().superRefine((x, ctx) => {
        const schemas = [
          z.literal('staged'),
          z.literal('importing'),
          z.literal('imported'),
          z.literal('discarded'),
        ];
        const errors = schemas.reduce<z.ZodError[]>(
          (errors, schema) =>
            ((result) => (result.error ? [...errors, result.error] : errors))(
              schema.safeParse(x)
            ),
          []
        );
        if (schemas.length - errors.length !== 1) {
          ctx.addIssue({
            path: ctx.path,
            code: 'invalid_union',
            unionErrors: errors,
            message: 'Invalid input: Should pass single schema',
          });
        }
      }),
      z.null(),
    ])
    .optional(),
});

export const ListImportEntitiesResponse = z.object({
  entities: z.array(
    z.object({
      entityId: z.union([z.string(), z.null()]).optional(),
      entityType: z.union([z.string(), z.null()]).optional(),
      foreignId: z.string(),
      id: z.string().uuid(),
      importedByTeammate: z.boolean(),
      label: z.string(),
      source: z.any().superRefine((x, ctx) => {
        const schemas = [
          z.literal('linear'),
          z.literal('notion'),
          z.literal('slack'),
        ];
        const errors = schemas.reduce<z.ZodError[]>(
          (errors, schema) =>
            ((result) => (result.error ? [...errors, result.error] : errors))(
              schema.safeParse(x)
            ),
          []
        );
        if (schemas.length - errors.length !== 1) {
          ctx.addIssue({
            path: ctx.path,
            code: 'invalid_union',
            unionErrors: errors,
            message: 'Invalid input: Should pass single schema',
          });
        }
      }),
      status: z.any().superRefine((x, ctx) => {
        const schemas = [
          z.literal('staged'),
          z.literal('importing'),
          z.literal('imported'),
          z.literal('discarded'),
        ];
        const errors = schemas.reduce<z.ZodError[]>(
          (errors, schema) =>
            ((result) => (result.error ? [...errors, result.error] : errors))(
              schema.safeParse(x)
            ),
          []
        );
        if (schemas.length - errors.length !== 1) {
          ctx.addIssue({
            path: ctx.path,
            code: 'invalid_union',
            unionErrors: errors,
            message: 'Invalid input: Should pass single schema',
          });
        }
      }),
    })
  ),
});

export const ListInboxes = z.record(z.any());

export const ListInboxesResponse = z.object({
  inboxes: z.array(
    z.object({
      emailAddress: z.string(),
      isDelegated: z.boolean(),
      isPrimary: z.boolean(),
    })
  ),
  summary: z.string(),
});

export const ListLabels = z.object({
  inbox: z.union([z.string(), z.null()]).optional(),
  thread_id: z.union([z.string().uuid(), z.null()]).optional(),
});

export const ListLabelsResponse = z.object({
  labels: z.array(
    z.object({ id: z.string().uuid(), name: z.string(), type: z.string() })
  ),
  summary: z.string(),
});

export const ListNotifications = z.object({
  done: z.union([z.boolean(), z.null()]).optional(),
  entities: z
    .union([
      z.array(
        z.object({
          entityType: z.enum([
            'email',
            'message',
            'channel',
            'document',
            'project',
            'chat',
            'call',
            'task',
            'github',
            'reminder',
            'calendar',
          ]),
          id: z.string(),
        })
      ),
      z.null(),
    ])
    .optional(),
  includeTypes: z
    .union([
      z.array(
        z.enum([
          'email',
          'message',
          'channel',
          'document',
          'project',
          'chat',
          'call',
          'task',
          'github',
          'reminder',
          'calendar',
        ])
      ),
      z.null(),
    ])
    .optional(),
  limit: z.union([z.number().int().gte(0), z.null()]).optional(),
  seen: z.union([z.boolean(), z.null()]).optional(),
});

export const ListNotificationsResponse = z.object({
  hasMore: z.boolean(),
  notifications: z.array(
    z.object({
      createdAt: z.string(),
      done: z.boolean(),
      entityId: z.string(),
      entityType: z.string(),
      eventType: z.string(),
      id: z.string().uuid(),
      metadata: z.any(),
      seen: z.boolean(),
      senderId: z.union([z.string(), z.null()]).optional(),
    })
  ),
});

export const ListSkills = z.record(z.any());

export const ListSkillsResponse = z.object({
  results: z.array(
    z.object({
      documentId: z.string().uuid(),
      name: z.string(),
      updatedAt: z
        .union([z.string().datetime({ offset: true }), z.null()])
        .optional(),
    })
  ),
});

export const ListTags = z.record(z.any());

export const ListTagsResponse = z.object({
  summary: z.string(),
  tagSets: z.array(
    z.object({
      propertyDefinitionId: z.string().uuid(),
      scope: z.any().superRefine((x, ctx) => {
        const schemas = [z.literal('personal'), z.literal('team')];
        const errors = schemas.reduce<z.ZodError[]>(
          (errors, schema) =>
            ((result) => (result.error ? [...errors, result.error] : errors))(
              schema.safeParse(x)
            ),
          []
        );
        if (schemas.length - errors.length !== 1) {
          ctx.addIssue({
            path: ctx.path,
            code: 'invalid_union',
            unionErrors: errors,
            message: 'Invalid input: Should pass single schema',
          });
        }
      }),
      tags: z.array(
        z.object({
          color: z.union([z.string(), z.null()]).optional(),
          id: z.string().uuid(),
          label: z.string(),
        })
      ),
    })
  ),
});

export const ListTeamMembers = z.record(z.any());

export const ListTeamMembersResponse = z.object({
  invited: z.array(z.object({ email: z.string(), role: z.string() })),
  members: z.array(z.object({ role: z.string(), userId: z.string() })),
});

export const LoadTools = z.object({ names: z.array(z.string()) });

export const LoadToolsResponse = z.object({
  loaded: z.array(z.object({ description: z.string(), name: z.string() })),
  not_found: z.array(z.string()),
});

export const MarkNotificationsDone = z.object({
  done: z.boolean(),
  notificationIds: z.array(z.string().uuid()),
});

export const MarkNotificationsResponse = z.object({
  count: z.number().int().gte(0),
  success: z.boolean(),
});

export const MarkNotificationsSeen = z.object({
  notificationIds: z.array(z.string().uuid()),
});

export const MoveToProject = z.object({
  entityId: z.string().uuid(),
  entityType: z.any().superRefine((x, ctx) => {
    const schemas = [
      z.literal('document'),
      z.literal('chat'),
      z.literal('email'),
      z.literal('project'),
    ];
    const errors = schemas.reduce<z.ZodError[]>(
      (errors, schema) =>
        ((result) => (result.error ? [...errors, result.error] : errors))(
          schema.safeParse(x)
        ),
      []
    );
    if (schemas.length - errors.length !== 1) {
      ctx.addIssue({
        path: ctx.path,
        code: 'invalid_union',
        unionErrors: errors,
        message: 'Invalid input: Should pass single schema',
      });
    }
  }),
  projectId: z.union([z.string().uuid(), z.null()]).optional(),
});

export const MoveToProjectResponse = z.object({
  message: z.string(),
  success: z.boolean(),
});

export const NameSearch = z.object({
  entityTypes: z
    .array(
      z.enum([
        'documents',
        'chats',
        'emails',
        'channels',
        'projects',
        'call_records',
      ])
    )
    .optional(),
  inbox: z.union([z.string(), z.null()]).optional(),
  matchType: z
    .intersection(
      z.any().superRefine((x, ctx) => {
        const schemas = [z.literal('partial'), z.literal('exact')];
        const errors = schemas.reduce<z.ZodError[]>(
          (errors, schema) =>
            ((result) => (result.error ? [...errors, result.error] : errors))(
              schema.safeParse(x)
            ),
          []
        );
        if (schemas.length - errors.length !== 1) {
          ctx.addIssue({
            path: ctx.path,
            code: 'invalid_union',
            unionErrors: errors,
            message: 'Invalid input: Should pass single schema',
          });
        }
      }),
      z.any().default('partial')
    )
    .optional(),
  name: z.string(),
  tags: z
    .union([
      z.array(
        z.object({
          label: z.string(),
          scope: z
            .union([
              z.any().superRefine((x, ctx) => {
                const schemas = [z.literal('personal'), z.literal('team')];
                const errors = schemas.reduce<z.ZodError[]>(
                  (errors, schema) =>
                    ((result) =>
                      result.error ? [...errors, result.error] : errors)(
                      schema.safeParse(x)
                    ),
                  []
                );
                if (schemas.length - errors.length !== 1) {
                  ctx.addIssue({
                    path: ctx.path,
                    code: 'invalid_union',
                    unionErrors: errors,
                    message: 'Invalid input: Should pass single schema',
                  });
                }
              }),
              z.null(),
            ])
            .optional(),
        })
      ),
      z.null(),
    ])
    .optional(),
  tagsMatch: z
    .any()
    .superRefine((x, ctx) => {
      const schemas = [z.literal('any'), z.literal('all')];
      const errors = schemas.reduce<z.ZodError[]>(
        (errors, schema) =>
          ((result) => (result.error ? [...errors, result.error] : errors))(
            schema.safeParse(x)
          ),
        []
      );
      if (schemas.length - errors.length !== 1) {
        ctx.addIssue({
          path: ctx.path,
          code: 'invalid_union',
          unionErrors: errors,
          message: 'Invalid input: Should pass single schema',
        });
      }
    })
    .optional(),
});

export const ReadCallRecord = z.object({ callId: z.string().uuid() });

export const ReadCallRecordResponse = z.object({
  callId: z.string().uuid(),
  summary: z.union([z.string(), z.null()]).optional(),
  transcript: z.array(
    z.object({
      content: z.string(),
      diarizedSpeakerId: z.union([z.string(), z.null()]).optional(),
      endedAt: z
        .union([z.string().datetime({ offset: true }), z.null()])
        .optional(),
      speakerId: z.string(),
      startedAt: z.string().datetime({ offset: true }),
    })
  ),
});

export const ReadChannelMessageContext = z.object({
  channelAfter: z
    .union([z.number().int().gte(0).lte(65535), z.null()])
    .optional(),
  channelBefore: z
    .union([z.number().int().gte(0).lte(65535), z.null()])
    .optional(),
  channelId: z.string().uuid(),
  maxCharsPerMessage: z.union([z.number().int().gte(0), z.null()]).optional(),
  messageId: z.string().uuid(),
  threadAfter: z
    .union([z.number().int().gte(0).lte(65535), z.null()])
    .optional(),
  threadBefore: z
    .union([z.number().int().gte(0).lte(65535), z.null()])
    .optional(),
});

export const ReadChannelMessageContextResponse = z.object({
  anchor: z.object({
    channelId: z.string().uuid(),
    createdAt: z.string().datetime({ offset: true }),
    kind: z.any().superRefine((x, ctx) => {
      const schemas = [z.literal('topLevelMessage'), z.literal('threadReply')];
      const errors = schemas.reduce<z.ZodError[]>(
        (errors, schema) =>
          ((result) => (result.error ? [...errors, result.error] : errors))(
            schema.safeParse(x)
          ),
        []
      );
      if (schemas.length - errors.length !== 1) {
        ctx.addIssue({
          path: ctx.path,
          code: 'invalid_union',
          unionErrors: errors,
          message: 'Invalid input: Should pass single schema',
        });
      }
    }),
    messageId: z.string().uuid(),
    threadId: z.string().uuid(),
  }),
  channelContext: z.object({
    after: z.array(
      z.object({
        attachments: z.array(
          z.object({
            createdAt: z.string().datetime({ offset: true }),
            entityId: z.string(),
            entityType: z.string(),
            id: z.string().uuid(),
          })
        ),
        channelId: z.string().uuid(),
        content: z.string(),
        contentTruncated: z.boolean(),
        createdAt: z.string().datetime({ offset: true }),
        deletedAt: z
          .union([z.string().datetime({ offset: true }), z.null()])
          .optional(),
        editedAt: z
          .union([z.string().datetime({ offset: true }), z.null()])
          .optional(),
        id: z.string().uuid(),
        reactions: z.array(
          z.object({ emoji: z.string(), users: z.array(z.string()) })
        ),
        senderId: z.string(),
        thread: z.object({
          latestReplyAt: z
            .union([z.string().datetime({ offset: true }), z.null()])
            .optional(),
          omittedReplyCount: z.number().int(),
          preview: z
            .union([
              z.array(
                z.object({
                  attachments: z.array(
                    z.object({
                      createdAt: z.string().datetime({ offset: true }),
                      entityId: z.string(),
                      entityType: z.string(),
                      id: z.string().uuid(),
                    })
                  ),
                  content: z.string(),
                  contentTruncated: z.boolean(),
                  createdAt: z.string().datetime({ offset: true }),
                  editedAt: z
                    .union([z.string().datetime({ offset: true }), z.null()])
                    .optional(),
                  id: z.string().uuid(),
                  reactions: z.array(
                    z.object({ emoji: z.string(), users: z.array(z.string()) })
                  ),
                  senderId: z.string(),
                  threadId: z.string().uuid(),
                  updatedAt: z.string().datetime({ offset: true }),
                })
              ),
              z.null(),
            ])
            .optional(),
          replyCount: z.number().int(),
          threadId: z.string().uuid(),
        }),
        updatedAt: z.string().datetime({ offset: true }),
      })
    ),
    anchorOrParent: z.object({
      attachments: z.array(
        z.object({
          createdAt: z.string().datetime({ offset: true }),
          entityId: z.string(),
          entityType: z.string(),
          id: z.string().uuid(),
        })
      ),
      channelId: z.string().uuid(),
      content: z.string(),
      contentTruncated: z.boolean(),
      createdAt: z.string().datetime({ offset: true }),
      deletedAt: z
        .union([z.string().datetime({ offset: true }), z.null()])
        .optional(),
      editedAt: z
        .union([z.string().datetime({ offset: true }), z.null()])
        .optional(),
      id: z.string().uuid(),
      reactions: z.array(
        z.object({ emoji: z.string(), users: z.array(z.string()) })
      ),
      senderId: z.string(),
      thread: z.object({
        latestReplyAt: z
          .union([z.string().datetime({ offset: true }), z.null()])
          .optional(),
        omittedReplyCount: z.number().int(),
        preview: z
          .union([
            z.array(
              z.object({
                attachments: z.array(
                  z.object({
                    createdAt: z.string().datetime({ offset: true }),
                    entityId: z.string(),
                    entityType: z.string(),
                    id: z.string().uuid(),
                  })
                ),
                content: z.string(),
                contentTruncated: z.boolean(),
                createdAt: z.string().datetime({ offset: true }),
                editedAt: z
                  .union([z.string().datetime({ offset: true }), z.null()])
                  .optional(),
                id: z.string().uuid(),
                reactions: z.array(
                  z.object({ emoji: z.string(), users: z.array(z.string()) })
                ),
                senderId: z.string(),
                threadId: z.string().uuid(),
                updatedAt: z.string().datetime({ offset: true }),
              })
            ),
            z.null(),
          ])
          .optional(),
        replyCount: z.number().int(),
        threadId: z.string().uuid(),
      }),
      updatedAt: z.string().datetime({ offset: true }),
    }),
    before: z.array(
      z.object({
        attachments: z.array(
          z.object({
            createdAt: z.string().datetime({ offset: true }),
            entityId: z.string(),
            entityType: z.string(),
            id: z.string().uuid(),
          })
        ),
        channelId: z.string().uuid(),
        content: z.string(),
        contentTruncated: z.boolean(),
        createdAt: z.string().datetime({ offset: true }),
        deletedAt: z
          .union([z.string().datetime({ offset: true }), z.null()])
          .optional(),
        editedAt: z
          .union([z.string().datetime({ offset: true }), z.null()])
          .optional(),
        id: z.string().uuid(),
        reactions: z.array(
          z.object({ emoji: z.string(), users: z.array(z.string()) })
        ),
        senderId: z.string(),
        thread: z.object({
          latestReplyAt: z
            .union([z.string().datetime({ offset: true }), z.null()])
            .optional(),
          omittedReplyCount: z.number().int(),
          preview: z
            .union([
              z.array(
                z.object({
                  attachments: z.array(
                    z.object({
                      createdAt: z.string().datetime({ offset: true }),
                      entityId: z.string(),
                      entityType: z.string(),
                      id: z.string().uuid(),
                    })
                  ),
                  content: z.string(),
                  contentTruncated: z.boolean(),
                  createdAt: z.string().datetime({ offset: true }),
                  editedAt: z
                    .union([z.string().datetime({ offset: true }), z.null()])
                    .optional(),
                  id: z.string().uuid(),
                  reactions: z.array(
                    z.object({ emoji: z.string(), users: z.array(z.string()) })
                  ),
                  senderId: z.string(),
                  threadId: z.string().uuid(),
                  updatedAt: z.string().datetime({ offset: true }),
                })
              ),
              z.null(),
            ])
            .optional(),
          replyCount: z.number().int(),
          threadId: z.string().uuid(),
        }),
        updatedAt: z.string().datetime({ offset: true }),
      })
    ),
  }),
  omissions: z.array(
    z.object({
      count: z.union([z.number().int(), z.null()]).optional(),
      cursor: z.union([z.string(), z.null()]).optional(),
      kind: z.any().superRefine((x, ctx) => {
        const schemas = [
          z.literal('olderMessages'),
          z.literal('newerMessages'),
          z.literal('threadReplies'),
          z.literal('truncatedContent'),
        ];
        const errors = schemas.reduce<z.ZodError[]>(
          (errors, schema) =>
            ((result) => (result.error ? [...errors, result.error] : errors))(
              schema.safeParse(x)
            ),
          []
        );
        if (schemas.length - errors.length !== 1) {
          ctx.addIssue({
            path: ctx.path,
            code: 'invalid_union',
            unionErrors: errors,
            message: 'Invalid input: Should pass single schema',
          });
        }
      }),
      messageId: z.union([z.string().uuid(), z.null()]).optional(),
      threadId: z.union([z.string().uuid(), z.null()]).optional(),
    })
  ),
  threadContext: z
    .union([
      z.object({
        anchorReply: z
          .union([
            z.object({
              attachments: z.array(
                z.object({
                  createdAt: z.string().datetime({ offset: true }),
                  entityId: z.string(),
                  entityType: z.string(),
                  id: z.string().uuid(),
                })
              ),
              content: z.string(),
              contentTruncated: z.boolean(),
              createdAt: z.string().datetime({ offset: true }),
              editedAt: z
                .union([z.string().datetime({ offset: true }), z.null()])
                .optional(),
              id: z.string().uuid(),
              reactions: z.array(
                z.object({ emoji: z.string(), users: z.array(z.string()) })
              ),
              senderId: z.string(),
              threadId: z.string().uuid(),
              updatedAt: z.string().datetime({ offset: true }),
            }),
            z.null(),
          ])
          .optional(),
        omittedAfter: z.number().int().gte(0),
        omittedBefore: z.number().int().gte(0),
        parent: z.object({
          attachments: z.array(
            z.object({
              createdAt: z.string().datetime({ offset: true }),
              entityId: z.string(),
              entityType: z.string(),
              id: z.string().uuid(),
            })
          ),
          channelId: z.string().uuid(),
          content: z.string(),
          contentTruncated: z.boolean(),
          createdAt: z.string().datetime({ offset: true }),
          deletedAt: z
            .union([z.string().datetime({ offset: true }), z.null()])
            .optional(),
          editedAt: z
            .union([z.string().datetime({ offset: true }), z.null()])
            .optional(),
          id: z.string().uuid(),
          reactions: z.array(
            z.object({ emoji: z.string(), users: z.array(z.string()) })
          ),
          senderId: z.string(),
          thread: z.object({
            latestReplyAt: z
              .union([z.string().datetime({ offset: true }), z.null()])
              .optional(),
            omittedReplyCount: z.number().int(),
            preview: z
              .union([
                z.array(
                  z.object({
                    attachments: z.array(
                      z.object({
                        createdAt: z.string().datetime({ offset: true }),
                        entityId: z.string(),
                        entityType: z.string(),
                        id: z.string().uuid(),
                      })
                    ),
                    content: z.string(),
                    contentTruncated: z.boolean(),
                    createdAt: z.string().datetime({ offset: true }),
                    editedAt: z
                      .union([z.string().datetime({ offset: true }), z.null()])
                      .optional(),
                    id: z.string().uuid(),
                    reactions: z.array(
                      z.object({
                        emoji: z.string(),
                        users: z.array(z.string()),
                      })
                    ),
                    senderId: z.string(),
                    threadId: z.string().uuid(),
                    updatedAt: z.string().datetime({ offset: true }),
                  })
                ),
                z.null(),
              ])
              .optional(),
            replyCount: z.number().int(),
            threadId: z.string().uuid(),
          }),
          updatedAt: z.string().datetime({ offset: true }),
        }),
        repliesAfter: z.array(
          z.object({
            attachments: z.array(
              z.object({
                createdAt: z.string().datetime({ offset: true }),
                entityId: z.string(),
                entityType: z.string(),
                id: z.string().uuid(),
              })
            ),
            content: z.string(),
            contentTruncated: z.boolean(),
            createdAt: z.string().datetime({ offset: true }),
            editedAt: z
              .union([z.string().datetime({ offset: true }), z.null()])
              .optional(),
            id: z.string().uuid(),
            reactions: z.array(
              z.object({ emoji: z.string(), users: z.array(z.string()) })
            ),
            senderId: z.string(),
            threadId: z.string().uuid(),
            updatedAt: z.string().datetime({ offset: true }),
          })
        ),
        repliesBefore: z.array(
          z.object({
            attachments: z.array(
              z.object({
                createdAt: z.string().datetime({ offset: true }),
                entityId: z.string(),
                entityType: z.string(),
                id: z.string().uuid(),
              })
            ),
            content: z.string(),
            contentTruncated: z.boolean(),
            createdAt: z.string().datetime({ offset: true }),
            editedAt: z
              .union([z.string().datetime({ offset: true }), z.null()])
              .optional(),
            id: z.string().uuid(),
            reactions: z.array(
              z.object({ emoji: z.string(), users: z.array(z.string()) })
            ),
            senderId: z.string(),
            threadId: z.string().uuid(),
            updatedAt: z.string().datetime({ offset: true }),
          })
        ),
        threadId: z.string().uuid(),
      }),
      z.null(),
    ])
    .optional(),
});

export const ReadChannelMessages = z.object({
  channelId: z.string().uuid(),
  cursor: z.union([z.string(), z.null()]).optional(),
  direction: z.union([z.enum(['older', 'newer']), z.null()]).optional(),
  from: z.union([z.string().datetime({ offset: true }), z.null()]).optional(),
  includeThreadPreviews: z.union([z.boolean(), z.null()]).optional(),
  limit: z.union([z.number().int().gte(0).lte(65535), z.null()]).optional(),
  maxCharsPerMessage: z.union([z.number().int().gte(0), z.null()]).optional(),
  messageId: z.union([z.string().uuid(), z.null()]).optional(),
  messageIds: z.array(z.string().uuid()).optional(),
  to: z.union([z.string().datetime({ offset: true }), z.null()]).optional(),
  windowType: z.enum([
    'latest',
    'timeRange',
    'aroundMessage',
    'page',
    'messages',
  ]),
});

export const ReadChannelMessagesResponse = z.object({
  channelId: z.string().uuid(),
  messages: z.array(
    z.object({
      attachments: z.array(
        z.object({
          createdAt: z.string().datetime({ offset: true }),
          entityId: z.string(),
          entityType: z.string(),
          id: z.string().uuid(),
        })
      ),
      channelId: z.string().uuid(),
      content: z.string(),
      contentTruncated: z.boolean(),
      createdAt: z.string().datetime({ offset: true }),
      deletedAt: z
        .union([z.string().datetime({ offset: true }), z.null()])
        .optional(),
      editedAt: z
        .union([z.string().datetime({ offset: true }), z.null()])
        .optional(),
      id: z.string().uuid(),
      reactions: z.array(
        z.object({ emoji: z.string(), users: z.array(z.string()) })
      ),
      senderId: z.string(),
      thread: z.object({
        latestReplyAt: z
          .union([z.string().datetime({ offset: true }), z.null()])
          .optional(),
        omittedReplyCount: z.number().int(),
        preview: z
          .union([
            z.array(
              z.object({
                attachments: z.array(
                  z.object({
                    createdAt: z.string().datetime({ offset: true }),
                    entityId: z.string(),
                    entityType: z.string(),
                    id: z.string().uuid(),
                  })
                ),
                content: z.string(),
                contentTruncated: z.boolean(),
                createdAt: z.string().datetime({ offset: true }),
                editedAt: z
                  .union([z.string().datetime({ offset: true }), z.null()])
                  .optional(),
                id: z.string().uuid(),
                reactions: z.array(
                  z.object({ emoji: z.string(), users: z.array(z.string()) })
                ),
                senderId: z.string(),
                threadId: z.string().uuid(),
                updatedAt: z.string().datetime({ offset: true }),
              })
            ),
            z.null(),
          ])
          .optional(),
        replyCount: z.number().int(),
        threadId: z.string().uuid(),
      }),
      updatedAt: z.string().datetime({ offset: true }),
    })
  ),
  navigation: z.object({
    hasMoreNewer: z.boolean(),
    hasMoreOlder: z.boolean(),
    newerCursor: z.union([z.string(), z.null()]).optional(),
    olderCursor: z.union([z.string(), z.null()]).optional(),
  }),
  omissions: z.array(
    z.object({
      count: z.union([z.number().int(), z.null()]).optional(),
      cursor: z.union([z.string(), z.null()]).optional(),
      kind: z.any().superRefine((x, ctx) => {
        const schemas = [
          z.literal('olderMessages'),
          z.literal('newerMessages'),
          z.literal('threadReplies'),
          z.literal('truncatedContent'),
        ];
        const errors = schemas.reduce<z.ZodError[]>(
          (errors, schema) =>
            ((result) => (result.error ? [...errors, result.error] : errors))(
              schema.safeParse(x)
            ),
          []
        );
        if (schemas.length - errors.length !== 1) {
          ctx.addIssue({
            path: ctx.path,
            code: 'invalid_union',
            unionErrors: errors,
            message: 'Invalid input: Should pass single schema',
          });
        }
      }),
      messageId: z.union([z.string().uuid(), z.null()]).optional(),
      threadId: z.union([z.string().uuid(), z.null()]).optional(),
    })
  ),
  window: z.object({
    direction: z.union([z.enum(['older', 'newer']), z.null()]).optional(),
    from: z.union([z.string().datetime({ offset: true }), z.null()]).optional(),
    messageId: z.union([z.string().uuid(), z.null()]).optional(),
    messageIds: z.array(z.string().uuid()),
    to: z.union([z.string().datetime({ offset: true }), z.null()]).optional(),
    windowType: z.enum([
      'latest',
      'timeRange',
      'aroundMessage',
      'page',
      'messages',
    ]),
  }),
});

export const ReadChannelThread = z.object({
  after: z.union([z.number().int().gte(0).lte(65535), z.null()]).optional(),
  before: z.union([z.number().int().gte(0).lte(65535), z.null()]).optional(),
  channelId: z.string().uuid(),
  includeChannelContext: z.union([z.boolean(), z.null()]).optional(),
  limit: z.union([z.number().int().gte(0).lte(65535), z.null()]).optional(),
  maxCharsPerMessage: z.union([z.number().int().gte(0), z.null()]).optional(),
  messageId: z.string().uuid(),
  replyId: z.union([z.string().uuid(), z.null()]).optional(),
  windowType: z
    .union([z.enum(['allIfSmall', 'latest', 'aroundReply']), z.null()])
    .optional(),
});

export const ReadChannelThreadResponse = z.object({
  anchor: z.object({
    channelId: z.string().uuid(),
    createdAt: z.string().datetime({ offset: true }),
    kind: z.any().superRefine((x, ctx) => {
      const schemas = [z.literal('topLevelMessage'), z.literal('threadReply')];
      const errors = schemas.reduce<z.ZodError[]>(
        (errors, schema) =>
          ((result) => (result.error ? [...errors, result.error] : errors))(
            schema.safeParse(x)
          ),
        []
      );
      if (schemas.length - errors.length !== 1) {
        ctx.addIssue({
          path: ctx.path,
          code: 'invalid_union',
          unionErrors: errors,
          message: 'Invalid input: Should pass single schema',
        });
      }
    }),
    messageId: z.string().uuid(),
    threadId: z.string().uuid(),
  }),
  channelContext: z
    .union([
      z.array(
        z.object({
          attachments: z.array(
            z.object({
              createdAt: z.string().datetime({ offset: true }),
              entityId: z.string(),
              entityType: z.string(),
              id: z.string().uuid(),
            })
          ),
          channelId: z.string().uuid(),
          content: z.string(),
          contentTruncated: z.boolean(),
          createdAt: z.string().datetime({ offset: true }),
          deletedAt: z
            .union([z.string().datetime({ offset: true }), z.null()])
            .optional(),
          editedAt: z
            .union([z.string().datetime({ offset: true }), z.null()])
            .optional(),
          id: z.string().uuid(),
          reactions: z.array(
            z.object({ emoji: z.string(), users: z.array(z.string()) })
          ),
          senderId: z.string(),
          thread: z.object({
            latestReplyAt: z
              .union([z.string().datetime({ offset: true }), z.null()])
              .optional(),
            omittedReplyCount: z.number().int(),
            preview: z
              .union([
                z.array(
                  z.object({
                    attachments: z.array(
                      z.object({
                        createdAt: z.string().datetime({ offset: true }),
                        entityId: z.string(),
                        entityType: z.string(),
                        id: z.string().uuid(),
                      })
                    ),
                    content: z.string(),
                    contentTruncated: z.boolean(),
                    createdAt: z.string().datetime({ offset: true }),
                    editedAt: z
                      .union([z.string().datetime({ offset: true }), z.null()])
                      .optional(),
                    id: z.string().uuid(),
                    reactions: z.array(
                      z.object({
                        emoji: z.string(),
                        users: z.array(z.string()),
                      })
                    ),
                    senderId: z.string(),
                    threadId: z.string().uuid(),
                    updatedAt: z.string().datetime({ offset: true }),
                  })
                ),
                z.null(),
              ])
              .optional(),
            replyCount: z.number().int(),
            threadId: z.string().uuid(),
          }),
          updatedAt: z.string().datetime({ offset: true }),
        })
      ),
      z.null(),
    ])
    .optional(),
  omissions: z.array(
    z.object({
      count: z.union([z.number().int(), z.null()]).optional(),
      cursor: z.union([z.string(), z.null()]).optional(),
      kind: z.any().superRefine((x, ctx) => {
        const schemas = [
          z.literal('olderMessages'),
          z.literal('newerMessages'),
          z.literal('threadReplies'),
          z.literal('truncatedContent'),
        ];
        const errors = schemas.reduce<z.ZodError[]>(
          (errors, schema) =>
            ((result) => (result.error ? [...errors, result.error] : errors))(
              schema.safeParse(x)
            ),
          []
        );
        if (schemas.length - errors.length !== 1) {
          ctx.addIssue({
            path: ctx.path,
            code: 'invalid_union',
            unionErrors: errors,
            message: 'Invalid input: Should pass single schema',
          });
        }
      }),
      messageId: z.union([z.string().uuid(), z.null()]).optional(),
      threadId: z.union([z.string().uuid(), z.null()]).optional(),
    })
  ),
  replies: z.array(
    z.object({
      attachments: z.array(
        z.object({
          createdAt: z.string().datetime({ offset: true }),
          entityId: z.string(),
          entityType: z.string(),
          id: z.string().uuid(),
        })
      ),
      content: z.string(),
      contentTruncated: z.boolean(),
      createdAt: z.string().datetime({ offset: true }),
      editedAt: z
        .union([z.string().datetime({ offset: true }), z.null()])
        .optional(),
      id: z.string().uuid(),
      reactions: z.array(
        z.object({ emoji: z.string(), users: z.array(z.string()) })
      ),
      senderId: z.string(),
      threadId: z.string().uuid(),
      updatedAt: z.string().datetime({ offset: true }),
    })
  ),
  thread: z.object({
    channelId: z.string().uuid(),
    latestReplyAt: z
      .union([z.string().datetime({ offset: true }), z.null()])
      .optional(),
    parent: z.object({
      attachments: z.array(
        z.object({
          createdAt: z.string().datetime({ offset: true }),
          entityId: z.string(),
          entityType: z.string(),
          id: z.string().uuid(),
        })
      ),
      channelId: z.string().uuid(),
      content: z.string(),
      contentTruncated: z.boolean(),
      createdAt: z.string().datetime({ offset: true }),
      deletedAt: z
        .union([z.string().datetime({ offset: true }), z.null()])
        .optional(),
      editedAt: z
        .union([z.string().datetime({ offset: true }), z.null()])
        .optional(),
      id: z.string().uuid(),
      reactions: z.array(
        z.object({ emoji: z.string(), users: z.array(z.string()) })
      ),
      senderId: z.string(),
      thread: z.object({
        latestReplyAt: z
          .union([z.string().datetime({ offset: true }), z.null()])
          .optional(),
        omittedReplyCount: z.number().int(),
        preview: z
          .union([
            z.array(
              z.object({
                attachments: z.array(
                  z.object({
                    createdAt: z.string().datetime({ offset: true }),
                    entityId: z.string(),
                    entityType: z.string(),
                    id: z.string().uuid(),
                  })
                ),
                content: z.string(),
                contentTruncated: z.boolean(),
                createdAt: z.string().datetime({ offset: true }),
                editedAt: z
                  .union([z.string().datetime({ offset: true }), z.null()])
                  .optional(),
                id: z.string().uuid(),
                reactions: z.array(
                  z.object({ emoji: z.string(), users: z.array(z.string()) })
                ),
                senderId: z.string(),
                threadId: z.string().uuid(),
                updatedAt: z.string().datetime({ offset: true }),
              })
            ),
            z.null(),
          ])
          .optional(),
        replyCount: z.number().int(),
        threadId: z.string().uuid(),
      }),
      updatedAt: z.string().datetime({ offset: true }),
    }),
    replyCount: z.number().int().gte(0),
    threadId: z.string().uuid(),
  }),
});

export const ReadChat = z.object({ chatId: z.string() });

export const ReadChatResponse = z.object({
  chatId: z.string(),
  messages: z.array(
    z.object({
      attachmentIds: z.array(z.string()),
      content: z.string(),
      role: z.string(),
    })
  ),
  title: z.string(),
});

export const ReadContent = z.object({ documentId: z.string().uuid() });

export const ReadContentResponse = z.object({
  comments: z.array(
    z.object({
      comments: z.array(
        z.object({
          commentId: z.number().int(),
          createdAt: z
            .union([z.string().datetime({ offset: true }), z.null()])
            .optional(),
          deletedAt: z
            .union([z.string().datetime({ offset: true }), z.null()])
            .optional(),
          metadata: z.any().optional(),
          order: z.union([z.number().int(), z.null()]).optional(),
          owner: z.string(),
          sender: z.union([z.string(), z.null()]).optional(),
          text: z.string(),
          threadId: z.number().int(),
          updatedAt: z
            .union([z.string().datetime({ offset: true }), z.null()])
            .optional(),
        })
      ),
      thread: z.object({
        createdAt: z
          .union([z.string().datetime({ offset: true }), z.null()])
          .optional(),
        deletedAt: z
          .union([z.string().datetime({ offset: true }), z.null()])
          .optional(),
        documentId: z.string(),
        metadata: z.any().optional(),
        owner: z.string(),
        resolved: z.boolean(),
        threadId: z.number().int(),
        updatedAt: z
          .union([z.string().datetime({ offset: true }), z.null()])
          .optional(),
      }),
    })
  ),
  content: z.any().superRefine((x, ctx) => {
    const schemas = [
      z.object({ text: z.string() }).strict(),
      z
        .object({
          markdown: z.array(
            z.any().superRefine((x, ctx) => {
              const schemas = [
                z.object({
                  content: z.string(),
                  nodeId: z.string(),
                  tag: z.string(),
                  type: z.literal('generic'),
                }),
                z.object({ type: z.literal('staticImage'), url: z.string() }),
                z.object({ id: z.string(), type: z.literal('dssImage') }),
              ];
              const errors = schemas.reduce<z.ZodError[]>(
                (errors, schema) =>
                  ((result) =>
                    result.error ? [...errors, result.error] : errors)(
                    schema.safeParse(x)
                  ),
                []
              );
              if (schemas.length - errors.length !== 1) {
                ctx.addIssue({
                  path: ctx.path,
                  code: 'invalid_union',
                  unionErrors: errors,
                  message: 'Invalid input: Should pass single schema',
                });
              }
            })
          ),
        })
        .strict(),
    ];
    const errors = schemas.reduce<z.ZodError[]>(
      (errors, schema) =>
        ((result) => (result.error ? [...errors, result.error] : errors))(
          schema.safeParse(x)
        ),
      []
    );
    if (schemas.length - errors.length !== 1) {
      ctx.addIssue({
        path: ctx.path,
        code: 'invalid_union',
        unionErrors: errors,
        message: 'Invalid input: Should pass single schema',
      });
    }
  }),
});

export const ReadMetadata = z.object({ documentId: z.string().uuid() });

export const ReadMetadataResponse = z.object({
  documentMetadata: z.object({
    branchName: z.union([z.string(), z.null()]).optional(),
    branchedFromId: z.union([z.string(), z.null()]).optional(),
    branchedFromVersionId: z.union([z.number().int(), z.null()]).optional(),
    content: z.object({
      location: z
        .union([
          z.any().superRefine((x, ctx) => {
            const schemas = [
              z.literal('object_storage'),
              z.literal('sync_service'),
              z.literal('docx_bom_parts'),
              z.literal('converted_pdf'),
              z.literal('unknown'),
            ];
            const errors = schemas.reduce<z.ZodError[]>(
              (errors, schema) =>
                ((result) =>
                  result.error ? [...errors, result.error] : errors)(
                  schema.safeParse(x)
                ),
              []
            );
            if (schemas.length - errors.length !== 1) {
              ctx.addIssue({
                path: ctx.path,
                code: 'invalid_union',
                unionErrors: errors,
                message: 'Invalid input: Should pass single schema',
              });
            }
          }),
          z.null(),
        ])
        .optional(),
      state: z.any().superRefine((x, ctx) => {
        const schemas = [
          z.literal('unknown'),
          z.literal('pending'),
          z.literal('ready'),
        ];
        const errors = schemas.reduce<z.ZodError[]>(
          (errors, schema) =>
            ((result) => (result.error ? [...errors, result.error] : errors))(
              schema.safeParse(x)
            ),
          []
        );
        if (schemas.length - errors.length !== 1) {
          ctx.addIssue({
            path: ctx.path,
            code: 'invalid_union',
            unionErrors: errors,
            message: 'Invalid input: Should pass single schema',
          });
        }
      }),
    }),
    createdAt: z
      .union([z.string().datetime({ offset: true }), z.null()])
      .optional(),
    deletedAt: z
      .union([z.string().datetime({ offset: true }), z.null()])
      .optional(),
    documentBom: z.any().optional(),
    documentFamilyId: z.union([z.number().int(), z.null()]).optional(),
    documentId: z.string(),
    documentName: z.string(),
    documentVersionId: z.number().int(),
    fileType: z.union([z.string(), z.null()]).optional(),
    modificationData: z.any().optional(),
    owner: z.string(),
    projectId: z.union([z.string(), z.null()]).optional(),
    projectName: z.union([z.string(), z.null()]).optional(),
    sha: z.union([z.string(), z.null()]).optional(),
    subType: z
      .union([
        z.any().superRefine((x, ctx) => {
          const schemas = [
            z.literal('task'),
            z.literal('snippet'),
            z.literal('skill'),
          ];
          const errors = schemas.reduce<z.ZodError[]>(
            (errors, schema) =>
              ((result) => (result.error ? [...errors, result.error] : errors))(
                schema.safeParse(x)
              ),
            []
          );
          if (schemas.length - errors.length !== 1) {
            ctx.addIssue({
              path: ctx.path,
              code: 'invalid_union',
              unionErrors: errors,
              message: 'Invalid input: Should pass single schema',
            });
          }
        }),
        z.null(),
      ])
      .optional(),
    teamId: z.union([z.string(), z.null()]).optional(),
    teamTaskId: z.union([z.number().int(), z.null()]).optional(),
    updatedAt: z
      .union([z.string().datetime({ offset: true }), z.null()])
      .optional(),
  }),
  userAccessLevel: z.enum(['view', 'comment', 'edit', 'owner']),
});

export const ReadProject = z.object({ projectId: z.string().uuid() });

export const ReadProjectResponse = z.object({
  items: z.array(
    z.object({
      fileType: z.union([z.string(), z.null()]).optional(),
      id: z.string(),
      itemType: z.any().superRefine((x, ctx) => {
        const schemas = [
          z.literal('document'),
          z.literal('chat'),
          z.literal('project'),
        ];
        const errors = schemas.reduce<z.ZodError[]>(
          (errors, schema) =>
            ((result) => (result.error ? [...errors, result.error] : errors))(
              schema.safeParse(x)
            ),
          []
        );
        if (schemas.length - errors.length !== 1) {
          ctx.addIssue({
            path: ctx.path,
            code: 'invalid_union',
            unionErrors: errors,
            message: 'Invalid input: Should pass single schema',
          });
        }
      }),
      name: z.string(),
      updatedAt: z
        .union([z.string().datetime({ offset: true }), z.null()])
        .optional(),
    })
  ),
  parentProjectId: z.union([z.string(), z.null()]).optional(),
  projectId: z.string().uuid(),
  projectName: z.string(),
});

export const RenameDocument = z.object({
  documentId: z.string().uuid(),
  documentName: z.string(),
});

export const RenameDocumentResponse = z.object({
  documentId: z.string().uuid(),
  message: z.string(),
  success: z.boolean(),
});

export const SearchSkills = z.object({
  matchType: z
    .any()
    .superRefine((x, ctx) => {
      const schemas = [z.literal('partial'), z.literal('exact')];
      const errors = schemas.reduce<z.ZodError[]>(
        (errors, schema) =>
          ((result) => (result.error ? [...errors, result.error] : errors))(
            schema.safeParse(x)
          ),
        []
      );
      if (schemas.length - errors.length !== 1) {
        ctx.addIssue({
          path: ctx.path,
          code: 'invalid_union',
          unionErrors: errors,
          message: 'Invalid input: Should pass single schema',
        });
      }
    })
    .optional(),
  name: z.string(),
});

export const SearchSkillsResponse = z.object({
  results: z.array(
    z.object({
      documentId: z.string().uuid(),
      name: z.string(),
      updatedAt: z
        .union([z.string().datetime({ offset: true }), z.null()])
        .optional(),
    })
  ),
});

export const SearchTools = z.object({ query: z.string() });

export const SearchToolsResponse = z.object({
  additional_matches: z.array(
    z.object({ description: z.string(), name: z.string() })
  ),
  results: z.array(z.object({ description: z.string(), name: z.string() })),
});

export const SelfKnowledge = z.record(z.any());

export const SelfKnowledgeResponse = z.object({ about: z.string() });

export const SendChannelMessage = z.object({
  channel_id: z.string().uuid(),
  content: z.string(),
  thread_id: z.union([z.string().uuid(), z.null()]).optional(),
});

export const SendChannelMessageResponse = z.object({
  channel_id: z.string().uuid(),
  message_id: z.string(),
});

export const SendEmail = z.object({
  bcc: z
    .array(
      z.object({
        email: z.string(),
        name: z.union([z.string(), z.null()]).optional(),
      })
    )
    .optional(),
  body: z.string(),
  cc: z
    .array(
      z.object({
        email: z.string(),
        name: z.union([z.string(), z.null()]).optional(),
      })
    )
    .optional(),
  includeSignature: z.union([z.boolean(), z.null()]).optional(),
  replyingToId: z.union([z.string().uuid(), z.null()]).optional(),
  subject: z.string(),
  to: z.array(
    z.object({
      email: z.string(),
      name: z.union([z.string(), z.null()]).optional(),
    })
  ),
});

export const UserToolResponse = z.any().superRefine((x, ctx) => {
  const schemas = [
    z.literal('PendingUserExecution'),
    z.literal('Rejected'),
    z
      .object({
        UserAction: z.any().superRefine((x, ctx) => {
          const schemas = [
            z.literal('userEdited'),
            z
              .object({
                sent: z.object({
                  message_id: z.string().uuid(),
                  thread_id: z.string().uuid(),
                }),
              })
              .strict(),
            z
              .object({
                convertedToDraft: z.object({ draft_id: z.string().uuid() }),
              })
              .strict(),
          ];
          const errors = schemas.reduce<z.ZodError[]>(
            (errors, schema) =>
              ((result) => (result.error ? [...errors, result.error] : errors))(
                schema.safeParse(x)
              ),
            []
          );
          if (schemas.length - errors.length !== 1) {
            ctx.addIssue({
              path: ctx.path,
              code: 'invalid_union',
              unionErrors: errors,
              message: 'Invalid input: Should pass single schema',
            });
          }
        }),
      })
      .strict(),
  ];
  const errors = schemas.reduce<z.ZodError[]>(
    (errors, schema) =>
      ((result) => (result.error ? [...errors, result.error] : errors))(
        schema.safeParse(x)
      ),
    []
  );
  if (schemas.length - errors.length !== 1) {
    ctx.addIssue({
      path: ctx.path,
      code: 'invalid_union',
      unionErrors: errors,
      message: 'Invalid input: Should pass single schema',
    });
  }
});

export const SetEntityProperty = z.object({
  add_option_ids: z.union([z.array(z.string().uuid()), z.null()]).optional(),
  boolean_value: z.union([z.boolean(), z.null()]).optional(),
  date_value: z
    .union([z.string().datetime({ offset: true }), z.null()])
    .optional(),
  entity_id: z.string(),
  entity_ref: z
    .union([
      z.object({
        entityId: z.string(),
        entityType: z.enum([
          'document',
          'task',
          'project',
          'chat',
          'thread',
          'channel',
          'call',
          'user',
          'company',
        ]),
      }),
      z.null(),
    ])
    .optional(),
  entity_refs: z
    .union([
      z.array(
        z.object({
          entityId: z.string(),
          entityType: z.enum([
            'document',
            'task',
            'project',
            'chat',
            'thread',
            'channel',
            'call',
            'user',
            'company',
          ]),
        })
      ),
      z.null(),
    ])
    .optional(),
  entity_type: z.enum([
    'document',
    'project',
    'chat',
    'thread',
    'channel',
    'call',
    'user',
    'company',
  ]),
  link_url: z.union([z.string(), z.null()]).optional(),
  link_urls: z.union([z.array(z.string()), z.null()]).optional(),
  number_value: z.union([z.number(), z.null()]).optional(),
  option_id: z.union([z.string().uuid(), z.null()]).optional(),
  option_ids: z.union([z.array(z.string().uuid()), z.null()]).optional(),
  property_definition_id: z.string().uuid(),
  remove_option_ids: z.union([z.array(z.string().uuid()), z.null()]).optional(),
  string_value: z.union([z.string(), z.null()]).optional(),
});

export const SetEntityPropertyResponse = z.object({
  message: z.string(),
  success: z.boolean(),
});

export const Subagent = z.object({ task: z.string() });

export const SubagentResponse = z.object({ result: z.string() });

export const TextEditorCodeExecution = z.object({ input: z.string() });

export const TextEditorCodeExecutionResponse = z.object({
  content: z.any().superRefine((x, ctx) => {
    const schemas = [
      z.intersection(
        z.object({
          content: z.union([z.string(), z.null()]).optional(),
          file_type: z.union([z.string(), z.null()]).optional(),
          is_file_update: z.union([z.boolean(), z.null()]).optional(),
          lines: z.union([z.array(z.string()), z.null()]).optional(),
          newLines: z.union([z.number().int().gte(0), z.null()]).optional(),
          newStart: z.union([z.number().int().gte(0), z.null()]).optional(),
          numLines: z.union([z.number().int().gte(0), z.null()]).optional(),
          oldLines: z.union([z.number().int().gte(0), z.null()]).optional(),
          oldStart: z.union([z.number().int().gte(0), z.null()]).optional(),
          startLine: z.union([z.number().int().gte(0), z.null()]).optional(),
          totalLines: z.union([z.number().int().gte(0), z.null()]).optional(),
        }),
        z.object({ type: z.literal('text_editor_code_execution_view_result') })
      ),
      z.intersection(
        z.object({
          content: z.union([z.string(), z.null()]).optional(),
          file_type: z.union([z.string(), z.null()]).optional(),
          is_file_update: z.union([z.boolean(), z.null()]).optional(),
          lines: z.union([z.array(z.string()), z.null()]).optional(),
          newLines: z.union([z.number().int().gte(0), z.null()]).optional(),
          newStart: z.union([z.number().int().gte(0), z.null()]).optional(),
          numLines: z.union([z.number().int().gte(0), z.null()]).optional(),
          oldLines: z.union([z.number().int().gte(0), z.null()]).optional(),
          oldStart: z.union([z.number().int().gte(0), z.null()]).optional(),
          startLine: z.union([z.number().int().gte(0), z.null()]).optional(),
          totalLines: z.union([z.number().int().gte(0), z.null()]).optional(),
        }),
        z.object({
          type: z.literal('text_editor_code_execution_create_result'),
        })
      ),
      z.intersection(
        z.object({
          content: z.union([z.string(), z.null()]).optional(),
          file_type: z.union([z.string(), z.null()]).optional(),
          is_file_update: z.union([z.boolean(), z.null()]).optional(),
          lines: z.union([z.array(z.string()), z.null()]).optional(),
          newLines: z.union([z.number().int().gte(0), z.null()]).optional(),
          newStart: z.union([z.number().int().gte(0), z.null()]).optional(),
          numLines: z.union([z.number().int().gte(0), z.null()]).optional(),
          oldLines: z.union([z.number().int().gte(0), z.null()]).optional(),
          oldStart: z.union([z.number().int().gte(0), z.null()]).optional(),
          startLine: z.union([z.number().int().gte(0), z.null()]).optional(),
          totalLines: z.union([z.number().int().gte(0), z.null()]).optional(),
        }),
        z.object({
          type: z.literal('text_editor_code_execution_str_replace_result'),
        })
      ),
      z.intersection(
        z.object({
          error_code: z.any().superRefine((x, ctx) => {
            const schemas = [
              z.literal('unavailable'),
              z.literal('execution_time_exceeded'),
              z.literal('container_expired'),
              z.literal('invalid_input'),
              z.literal('too_many_requests'),
              z.literal('file_not_found'),
              z.literal('string_not_found'),
            ];
            const errors = schemas.reduce<z.ZodError[]>(
              (errors, schema) =>
                ((result) =>
                  result.error ? [...errors, result.error] : errors)(
                  schema.safeParse(x)
                ),
              []
            );
            if (schemas.length - errors.length !== 1) {
              ctx.addIssue({
                path: ctx.path,
                code: 'invalid_union',
                unionErrors: errors,
                message: 'Invalid input: Should pass single schema',
              });
            }
          }),
        }),
        z.object({
          type: z.literal('text_editor_code_execution_tool_result_error'),
        })
      ),
    ];
    const errors = schemas.reduce<z.ZodError[]>(
      (errors, schema) =>
        ((result) => (result.error ? [...errors, result.error] : errors))(
          schema.safeParse(x)
        ),
      []
    );
    if (schemas.length - errors.length !== 1) {
      ctx.addIssue({
        path: ctx.path,
        code: 'invalid_union',
        unionErrors: errors,
        message: 'Invalid input: Should pass single schema',
      });
    }
  }),
  tool_use_id: z.string(),
});

export const UpdateThreadLabels = z.object({
  add: z.boolean(),
  label_id: z.string().uuid(),
  thread_id: z.string().uuid(),
});

export const UpdateThreadLabelsResponse = z.object({
  failedCount: z.number().int().gte(0),
  successfulCount: z.number().int().gte(0),
  summary: z.string(),
});

export const WebFetch = z.object({ input: z.string() });

export const WebFetchResponse = z.object({
  content: z.any().superRefine((x, ctx) => {
    const schemas = [
      z.intersection(
        z.object({
          content: z.object({
            citations: z
              .union([z.object({ enabled: z.boolean() }), z.null()])
              .optional(),
            source: z.any().superRefine((x, ctx) => {
              const schemas = [
                z.object({
                  data: z.string(),
                  media_type: z.string(),
                  type: z.literal('text'),
                }),
                z.object({
                  data: z.string(),
                  media_type: z.string(),
                  type: z.literal('base64'),
                }),
              ];
              const errors = schemas.reduce<z.ZodError[]>(
                (errors, schema) =>
                  ((result) =>
                    result.error ? [...errors, result.error] : errors)(
                    schema.safeParse(x)
                  ),
                []
              );
              if (schemas.length - errors.length !== 1) {
                ctx.addIssue({
                  path: ctx.path,
                  code: 'invalid_union',
                  unionErrors: errors,
                  message: 'Invalid input: Should pass single schema',
                });
              }
            }),
            title: z.union([z.string(), z.null()]).optional(),
          }),
          retrieved_at: z.string(),
          url: z.string(),
        }),
        z.object({ type: z.literal('web_fetch_result') })
      ),
      z.intersection(
        z.object({
          error_code: z.any().superRefine((x, ctx) => {
            const schemas = [
              z.literal('invalid_input'),
              z.literal('url_too_long'),
              z.literal('url_not_allowed'),
              z.literal('url_not_accessible'),
              z.literal('too_many_requests'),
              z.literal('unsupported_content_type'),
              z.literal('max_uses_exceeded'),
              z.literal('unavailable'),
            ];
            const errors = schemas.reduce<z.ZodError[]>(
              (errors, schema) =>
                ((result) =>
                  result.error ? [...errors, result.error] : errors)(
                  schema.safeParse(x)
                ),
              []
            );
            if (schemas.length - errors.length !== 1) {
              ctx.addIssue({
                path: ctx.path,
                code: 'invalid_union',
                unionErrors: errors,
                message: 'Invalid input: Should pass single schema',
              });
            }
          }),
        }),
        z.object({ type: z.literal('web_fetch_tool_result_error') })
      ),
    ];
    const errors = schemas.reduce<z.ZodError[]>(
      (errors, schema) =>
        ((result) => (result.error ? [...errors, result.error] : errors))(
          schema.safeParse(x)
        ),
      []
    );
    if (schemas.length - errors.length !== 1) {
      ctx.addIssue({
        path: ctx.path,
        code: 'invalid_union',
        unionErrors: errors,
        message: 'Invalid input: Should pass single schema',
      });
    }
  }),
  tool_use_id: z.string(),
});

export const WebSearch = z.object({ input: z.string() });

export const WebSearchResponse = z.object({
  content: z.union([
    z.array(
      z.object({
        encrypted_content: z.union([z.string(), z.null()]).optional(),
        page_age: z.union([z.string(), z.null()]).optional(),
        title: z.string(),
        type: z.literal('web_search_result'),
        url: z.string(),
      })
    ),
    z.object({ error_code: z.string(), type: z.string() }),
  ]),
  tool_use_id: z.string(),
});

export const ReadThread = z.object({
  contentType: z.enum([
    'channel',
    'channel-message',
    'chat-thread',
    'chat-message',
    'project',
  ]),
  ids: z.array(z.string()),
  messagesSince: z
    .union([z.string().datetime({ offset: true }), z.null()])
    .optional(),
});

export const ReadResponse = z.object({
  content: z.any().superRefine((x, ctx) => {
    const schemas = [
      z.object({
        channel_id: z.string(),
        channel_name: z.union([z.string(), z.null()]).optional(),
        transcript: z.string(),
        type: z.literal('channel'),
      }),
      z.object({
        conversation: z.array(
          z.object({
            chat_id: z.string(),
            messages: z.array(
              z.object({
                attachmentIds: z.array(z.string()),
                content: z.string(),
                date: z.string().datetime({ offset: true }),
              })
            ),
            title: z.string(),
          })
        ),
        type: z.literal('chat'),
      }),
      z.object({
        formatted_preview: z.string(),
        type: z.literal('itemPreviews'),
      }),
    ];
    const errors = schemas.reduce<z.ZodError[]>(
      (errors, schema) =>
        ((result) => (result.error ? [...errors, result.error] : errors))(
          schema.safeParse(x)
        ),
      []
    );
    if (schemas.length - errors.length !== 1) {
      ctx.addIssue({
        path: ctx.path,
        code: 'invalid_union',
        unionErrors: errors,
        message: 'Invalid input: Should pass single schema',
      });
    }
  }),
});
